/**
 * Fixture API Consumer
 * 
 * This module validates and consumes fixture exports created by fixtureApi.
 * It provides type guards and parsing functions for cross-version compatibility.
 * 
 * The viewer imports this module to parse fixtures; projects never need this.
 */

import {
    singleFixtureBrand,
    fixtureGroupBrand,
    fixtureVariantsBrand,
    type FixtureExport,
    type SingleFixtureExport,
    type FixtureGroupExport,
    type FixtureVariantsExport,
    type DisplayMode,
    type StyleDefinition,
    type PropertySchema,
    type RenderReturn,
    type RenderContext,
} from './fixtureApi.js';

// ============================================================================
// Type guards (use these for runtime checks)
// Cross-version compatible thanks to Symbol.for() in fixtureApi.ts
// ============================================================================

/**
 * Type guard for SingleFixtureExport.
 * Uses the global symbol brand for cross-version compatibility.
 */
export function isSingleFixture(value: unknown): value is SingleFixtureExport {
  return (
    typeof value === 'object' &&
    value !== null &&
    singleFixtureBrand in value &&
    (value as Record<symbol, unknown>)[singleFixtureBrand] === true &&
    '_options' in value &&
    typeof (value as { _options: unknown })._options === 'object'
  );
}

/**
 * Type guard for FixtureGroupExport.
 * Uses the global symbol brand for cross-version compatibility.
 */
export function isFixtureGroup(value: unknown): value is FixtureGroupExport {
  return (
    typeof value === 'object' &&
    value !== null &&
    fixtureGroupBrand in value &&
    (value as Record<symbol, unknown>)[fixtureGroupBrand] === true &&
    '_entries' in value &&
    typeof (value as { _entries: unknown })._entries === 'object'
  );
}

/**
 * Type guard for FixtureVariantsExport.
 * Uses the global symbol brand for cross-version compatibility.
 */
export function isFixtureVariants(value: unknown): value is FixtureVariantsExport {
  return (
    typeof value === 'object' &&
    value !== null &&
    fixtureVariantsBrand in value &&
    (value as Record<symbol, unknown>)[fixtureVariantsBrand] === true &&
    '_variants' in value &&
    typeof (value as { _variants: unknown })._variants === 'object'
  );
}

/**
 * Type guard for any fixture export type.
 */
export function isFixtureExport(value: unknown): value is FixtureExport {
  return isSingleFixture(value) || isFixtureGroup(value) || isFixtureVariants(value);
}

// ============================================================================
// Parsing functions (validate and cast)
// ============================================================================

export type ParseResult<T> =
  | { success: true; data: T }
  | { success: false; error: string };

/**
 * Parses and validates a potential fixture export.
 * Returns a ParseResult with either the validated data or an error message.
 */
export function parseFixtureExport(value: unknown): ParseResult<FixtureExport> {
  if (isSingleFixture(value)) {
    return { success: true, data: value };
  }
  if (isFixtureGroup(value)) {
    return { success: true, data: value };
  }
  if (isFixtureVariants(value)) {
    return { success: true, data: value };
  }
  return { success: false, error: 'Value is not a valid fixture export (missing brand symbol or _options/_entries/_variants)' };
}

/**
 * Parses and validates a single fixture export.
 */
export function parseSingleFixture(value: unknown): ParseResult<SingleFixtureExport> {
  if (isSingleFixture(value)) {
    return { success: true, data: value };
  }
  return { success: false, error: 'Value is not a SingleFixtureExport' };
}

/**
 * Parses and validates a fixture group export.
 */
export function parseFixtureGroup(value: unknown): ParseResult<FixtureGroupExport> {
  if (isFixtureGroup(value)) {
    return { success: true, data: value };
  }
  return { success: false, error: 'Value is not a FixtureGroupExport' };
}

/**
 * Parses and validates a fixture variants export.
 */
export function parseFixtureVariants(value: unknown): ParseResult<FixtureVariantsExport> {
  if (isFixtureVariants(value)) {
    return { success: true, data: value };
  }
  return { success: false, error: 'Value is not a FixtureVariantsExport' };
}

// ============================================================================
// ComponentDefinition creation (moved from defineFixture.ts)
// ============================================================================

/**
 * Definition of a component fixture (internal representation).
 */
export interface ComponentDefinition {
  /** Unique identifier within the fixture file */
  readonly id: string;

  /** Display name shown in the tree */
  readonly name: string;

  /** Optional description for documentation */
  readonly description?: string;

  /** How to isolate this component from other components */
  readonly isolation: 'none' | 'shadow-dom' | 'iframe';

  /** How to display: page (device viewport) or component (natural size) */
  readonly displayMode: DisplayMode;

  /** Stylesheets to inject (into document head, shadow root, or iframe head depending on isolation mode) */
  readonly styles?: readonly StyleDefinition[];

  /** Background pattern for the preview canvas: 'light' (default) or 'dark' for dark transparent pattern */
  readonly background: 'light' | 'dark';

  /** Labels for categorization and filtering */
  readonly labels: readonly string[];

  /** Property schema for the component */
  readonly properties: readonly PropertySchema[];

  /** Source file path this fixture was defined in */
  readonly sourceFile: string;

  /**
   * Render the component into the container.
   * Returns an optional RenderResult or Promise for cleanup and async readiness.
   */
  render(container: HTMLElement, context: RenderContext): RenderReturn;
}

/**
 * Creates the component definition from a validated SingleFixtureExport.
 */
export function createComponentDefinition(
  fixture: SingleFixtureExport,
  id: string,
  name: string,
  sourceFile: string,
): ComponentDefinition {
  const options = fixture._options;
  return {
    id,
    name,
    description: options.description,
    isolation: options.isolation ?? 'none',
    displayMode: options.displayMode ?? { type: 'component' },
    styles: options.styles,
    background: options.background ?? 'light',
    labels: options.labels ?? [],
    properties: options.properties ?? [],
    sourceFile,
    render: options.render,
  };
}

/**
 * Type guard for checking if a value is a ComponentDefinition.
 * @deprecated Use isSingleFixture or isFixtureGroup instead
 */
export function isComponentDefinition(value: unknown): value is ComponentDefinition {
  return (
    typeof value === 'object' &&
    value !== null &&
    'render' in value &&
    typeof (value as ComponentDefinition).render === 'function' &&
    'id' in value &&
    'name' in value
  );
}
